import { Message } from '@ui';
import { JWTModel } from 'typings/profile/security';
import { jwtDecode } from 'jwt-decode';
import { getTimeOffset } from './localStorageHelpers';

export function tryToParseJSON(value: string) {
  const result: { success: boolean; value: unknown } = {
    success: true,
    value,
  };

  try {
    result.value = JSON.parse(value);
  } catch (err) {
    result.success = false;
  }

  return result;
}

// Generate unique id
export function generateUniqId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function enumToMap(enumeration: any): Map<string, string | number> {
  const map = new Map<string, string | number>();
  for (const key in enumeration) {
    //TypeScript does not allow enum keys to be numeric
    if (!isNaN(Number(key))) continue;

    const val = enumeration[key] as string | number;

    //TypeScript does not allow enum value to be null or undefined
    if (val !== undefined && val !== null) map.set(key, val);
  }

  return map;
}

// Generate all path array from current location pathname (from tree)
// e.g Input Value = /system/admin/user -> Result = ['/system', '/system/admin', '/system/admin/user']
export const getDefaultOpenedKeys = (pathName: string) => {
  const pathArray = pathName.split('/').filter((el) => el);
  if (pathArray.length <= 1) return [];

  let path = '';
  const result: string[] = [];

  pathArray.forEach((e, i) => {
    if (i === 0) {
      path = `/${e}`;
    } else {
      path = `${path}/${e}`;
    }

    result.push(path);
  });

  return result;
};

export function decodeJWTToken(token: string): JWTModel | null {
  if (!token) {
    return null;
  }

  try {
    const result = jwtDecode(token) as JWTModel;
    return result;
  } catch (error) {
    return null;
  }
}

export function isAccessTokenAlive(token: string) {
  const decodedToken = decodeJWTToken(token);

  const timeOffset = getTimeOffset();
  const numberTimeOffset = timeOffset ? Number(timeOffset) : 0;
  const adjustedCurrentTime = Date.now() + numberTimeOffset;

  return decodedToken ? decodedToken.exp * 1000 > adjustedCurrentTime : false;
}

// Utility function to compare versions
export const compareVersions = (
  frontendVersion: string,
  backendVersion: string,
) => {
  if (frontendVersion === backendVersion) {
    return false;
  }

  const [frontMajor, frontMinor, frontPatch] = frontendVersion
    .split('.')
    .map(Number);
  const [backMajor, backMinor, backPatch] = backendVersion
    .split('.')
    .map(Number);

  if (backMajor > frontMajor) {
    return true;
  }

  if (backMajor === frontMajor && backMinor > frontMinor) {
    return true;
  }

  if (
    backMajor === frontMajor &&
    backMinor === frontMinor &&
    backPatch > frontPatch
  ) {
    return true;
  }

  return false; // backend version is not greater
};

export const checkAppVersionAndRefreshIfNeeded = (
  backendVersion: string,
  tooltipMessage: string,
) => {
  const frontendVersion = process.env.REACT_APP_SWAGGER_VERSION;
  if (
    frontendVersion &&
    backendVersion &&
    compareVersions(frontendVersion || '', backendVersion)
  ) {
    Message.info(tooltipMessage);
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  }
};

export function formatBooleanValue(value: unknown) {
  if (typeof value === 'boolean') {
    return value;
  } else {
    return null;
  }
}

export const scrollTop = () => window.scrollTo(0, 0);
